import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactToPrint from 'react-to-print';
import { HelioCheckout } from '@heliofi/checkout-react';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';

import { ModalHOC } from 'common';
import { PageLayout, MoneyCell, DefaultButton, BasicModal } from 'common/components';
import { withTheme } from 'common/styling/theme';
import { TwoStepEmailForm, TwoStepGoogleForm } from 'auth/twoStepAuth/_components';
import { verifyWithdrawalRequest, verifyWithdrawalResend } from 'auth/twoStepAuth/_redux';

import UserAmountEditHistory from '../UserAmountEditHistory';
import PaymentsInfoPanel from '../PaymentsInfoPanel';
import PaymentProofForm from '../PaymentProofForm';
import SecondPaymentsForm from '../SecondPaymentsForm';
import LeanPay from '../LeanPay';

import { staticStyles, getDynamicStyles } from './style';

const PaymentsConfirmationPanel = ({
  payments,
  profile,
  loadPayments,
  getPaymentsInvoice,
  loading,
  match,
  redirect,
  addons,
  theme,
  showModal,
  cancelWithdrawal,
  isConfirmationMessageResent,
}) => {
  const onReloadClick = () => loadPayments(payments.id);
  const isDeposit = ['wallet_deposit', 'deposit'].includes(payments?.operation);
  const isWithdraw = ['wallet_withdraw', 'withdraw'].includes(payments?.operation);
  const hasAdditionalForm = !payments?.paymentMethodRequest?.requisitesForm.properties.null;

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [tfaModalOpen, setTfaModalOpen] = useState(false);
  const [copiedButtonId, setCopiedButtonId] = useState(null);
  const [redirectModalOpen, setRedirectModalOpen] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const componentRef = useRef(null);

  const handleTfaModalToggle = () => {
    setTfaModalOpen(isOpen => !isOpen);
  };

  // Save consent_id to backend after Lean authorization success
  const saveConsentToBackend = (consentId, customerId, callback) => {
    fetch('/api/payments/leantech/consent/save/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId,
        deposit_id: match.params.id,
        consent_id: consentId,
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log('Consent saved to backend successfully');
          if (callback && typeof callback === 'function') {
            callback(true);
          }
        } else {
          console.warn('Failed to save consent to backend:', data.error);
          if (callback && typeof callback === 'function') {
            callback(false);
          }
        }
      })
      .catch(error => {
        console.error('Error saving consent to backend:', error);
        if (callback && typeof callback === 'function') {
          callback(false);
        }
      });
  };

  useEffect(() => {
    loadPayments(match.params.id);

    // Check for successRedirectUrl in URL params
    const urlParams = queryString.parse(window.location.search);

    // Handle Lean authorization callback - save consent_id and auto-click continue
    if (urlParams.lean_auth_status === 'success' && urlParams.consent_id) {
      const consentId = urlParams.consent_id;
      const customerId = urlParams.customer_id || '';

      // Save consent_id to backend first
      saveConsentToBackend(consentId, customerId, () => {
        // After saving, trigger the continue button click for LeanPay payments
        if (payments) {
          // Auto-click continue button after a short delay to ensure UI is ready
          setTimeout(() => {
            const continueButton = document.querySelector('[textid="continueRequest"]');
            if (continueButton) {
              continueButton.click();
            }
          }, 500);
        }
      });
      return;
    }

    // Handle failed Lean authorization
    if (urlParams.lean_auth_status === 'fail') {
      console.warn('Lean authorization failed');
      // Could show an error message here
      return;
    }

    if (urlParams.successRedirectUrl || urlParams.status === 'success') {
      // Open leanpay modal to handle redirect
      // Pass the current URL parameters to leanpay.html
      const leanpayUrl = `/client/pay?deposit_id=${match.params.id}&${window.location.search.substring(1)}`;
      setRedirectUrl(leanpayUrl);
      setRedirectModalOpen(true);
    }
  }, [match.params.id]);

  useEffect(() => {
    if (isConfirmationMessageResent) setMessageModalOpen(true);
  }, [isConfirmationMessageResent]);

  const submitPayments = () => {
    if (payments.redirectSpec.spec) {
      const { fields, url, method = 'POST' } = payments.redirectSpec.spec;

      // Проверяем, существует ли элемент формы, и если да, удаляем его
      const existingForm = document.forms.submitRequest;
      if (existingForm) {
        existingForm.parentNode.removeChild(existingForm);
      }

      let form = `<form action="${url}" name="submitRequest" method=${method} style="display: none;">`;
      Object.keys(fields).forEach(key => {
        form += `<input type="text" name="${key}" value="${fields[key]}" />`;
      });
      form += '</form>';

      const formEl = document.createElement('div');
      formEl.innerHTML = form;
      document.body.appendChild(formEl);
      document.forms.submitRequest.submit();
    }
  };

  const cancelWithdrawalClick = () => {
    showModal({
      caption: { id: 'confirmationToCancelWithdrawal' },
      content: {
        id: 'confirmText',
      },
      actionButton: {
        textId: 'justYes',
        handler: () => cancelWithdrawal(payments.id),
        type: 'error',
      },
      cancelButton: {
        textId: 'justNo',
      },
    });
  };

  const finishPayment = () => {
    setInfoModalOpen(true);
  };
  const closeModal = () => {
    setInfoModalOpen(false);
    setTimeout(() => {
      loadPayments(match.params.id);
    }, 1000);
  };

  useEffect(() => {
    if (redirect) {
      window.location = '/';
    }
  }, [redirect]);

  const captionSlug = () => {
    if (isDeposit) {
      return 'clientDeposit';
    }
    if (isWithdraw) {
      return 'clientWithdraw';
    }
    return 'justCompensation';
  };

  const openRedirectModal = url => {
    setRedirectUrl(url);
    setRedirectModalOpen(true);
  };

  const closeRedirectModal = () => {
    setRedirectModalOpen(false);
    setRedirectUrl('');
  };

  const closeLeanpayModal = () => {
    setRedirectModalOpen(false);
    setRedirectUrl('');
    // Reload payment info after closing
    loadPayments(match.params.id);
  };
  // 2. 定义处理消息的函数
  const handleIframeMessage = event => {
    // ** 重要的安全检查！**
    // 确保消息来自您信任的域名
    if (!(event.origin.includes('localhost') || event.origin.includes('dolmarkets'))) {
      return;
    }
    // 检查消息内容
    if (event.data && event.data.command === 'CLOSE_MODAL') {
      closeRedirectModal(); // 更新 State，React 会自动重新渲染并关闭弹框
      closeLeanpayModal(); // 同时关闭 leanpay 弹框
    }
  };

  // 3. 挂载和移除事件监听器
  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);

    // 组件卸载时移除监听器，防止内存泄漏
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, []); // 仅在组件挂载和卸载时执行，确保每次组件挂载时都添加监听器
  const paymentFormVals = {
    id: payments?.paymentMethodRequest?.id,
    invoiceEnabled: payments?.paymentMethodRequest?.invoiceEnabled,
    paymentMethod: payments?.paymentMethodRequest?.paymentMethod,
    request: payments?.paymentMethodRequest?.request,
    saveRequisites: payments?.paymentMethodRequest?.saveRequisites,
    requisitesForm: payments?.paymentMethodRequest?.requisitesForm,
  };

  const showPaymentNotification =
    (payments?.redirectSpec && payments.redirectSpec.type !== 'image') ||
    (payments?.redirectSpec && payments.redirectSpec.type === 'image' && payments?.redirectSpec.spec.url);

  return (
    payments &&
    Number(match.params.id) === payments.id && (
      <PageLayout
        captionSlug={captionSlug()}
        extraCaptionText={payments.id || ''}
        showReload
        onReloadClick={onReloadClick}
        reloading={loading}>
        {showPaymentNotification && (
          <FormattedMessage id="justPaymentNotificate">{txt => <h3>{txt}</h3>}</FormattedMessage>
        )}
        {/* eslint-disable */}
        <div ref={componentRef}>
          {/* eslint-enable */}
          <PaymentsInfoPanel
            isDeposit={isDeposit}
            data={payments}
            addons={addons}
            paymentSystemInstruction={payments.paymentSystemInstruction}
          />
          {payments.instruction && (
            <ReactToPrint
              trigger={() => (
                <div
                  className="Payments__instruction"
                  dangerouslySetInnerHTML={{
                    __html: payments.instruction.text,
                  }}
                />
              )}
              content={() => componentRef}
            />
          )}

          {payments.redirectSpec && payments.redirectSpec.type === 'image' && (
            <>
              <div className="Payments__type-image-spec-wrap">
                {payments.redirectSpec.spec.image && (
                  <img src={`data:image/png;base64, ${payments.redirectSpec.spec.image}`} alt="crypto qr" />
                )}
                {payments.redirectSpec.spec.text && (
                  <div>
                    <FormattedMessage id="justInfo">{txt => <span>{txt}</span>} </FormattedMessage>
                    {payments.paymentSystemSlug !== 'BitHide' && payments.paymentSystemSlug !== 'RDD' ? (
                      <div>
                        <div dangerouslySetInnerHTML={{ __html: payments.redirectSpec.spec.text }} />
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <text>
                            <FormattedMessage id="justWalletType" defaultProps="Wallet Type" />：
                          </text>
                          <div
                            style={{ marginRight: '10px' }}
                            dangerouslySetInnerHTML={{ __html: payments.redirectSpec.spec.type }}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <text>
                            <FormattedMessage id="justWalletAddress" defaultProps="Wallet address" />：
                          </text>
                          <div
                            style={{ marginRight: '10px' }}
                            dangerouslySetInnerHTML={{ __html: payments.redirectSpec.spec.address }}
                          />
                          <DefaultButton
                            textId={copiedButtonId === 'text' ? 'justCopiedWord' : 'justCopy'}
                            style={{
                              marginLeft: '50px',
                              border: '1px solid #BCBFC5',
                              backgroundColor: '#BCBFC5',
                              color: '#FFFFFF',
                              borderRadius: '15px',
                            }}
                            onClick={() => {
                              navigator.clipboard
                                .writeText(payments.redirectSpec.spec.address)
                                .then(() => {
                                  setCopiedButtonId('text');
                                  setTimeout(() => setCopiedButtonId(null), 2000);
                                })
                                .catch(err => {
                                  console.error('复制失败:', err);
                                });
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <text>
                            <FormattedMessage id="justTransactionAmount" defaultProps="Transaction amount" />：
                          </text>
                          <div
                            style={{ marginRight: '10px' }}
                            dangerouslySetInnerHTML={{ __html: payments.redirectSpec.spec.amount }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="Payments__buttons-wrap">
                {payments.redirectSpec.spec.url && (
                  <DefaultButton
                    textId="continueRequest"
                    onClick={() => {
                      const url = payments.redirectSpec.spec.url;
                      if (url && url.startsWith('/client/pay')) {
                        openRedirectModal(url);
                      } else {
                        window.open(url);
                      }
                    }}
                    filled
                  />
                )}
                {isDeposit && <DefaultButton textId="justBack" type="button" onClick={() => window.history.back()} />}
              </div>
            </>
          )}

          {payments.widget && payments.paymentSystemSlug === 'Helio' && (
            <div className="Payments__widget">
              <HelioCheckout config={payments.widget} />
            </div>
          )}

          <div className="Payments__buttons-wrap">
            {payments.invoiceEnabled && isDeposit && (
              <DefaultButton textId="downloadInvoice" onClick={() => getPaymentsInvoice(payments.id)} />
            )}

            {/* eslint-disable */}
            {payments.redirectSpec && payments.redirectSpec.type === 'form' && (
              <>
                <DefaultButton textId="continueRequest" onClick={submitPayments} filled />
                {isDeposit && <DefaultButton textId="justBack" type="button" onClick={() => window.history.back()} />}
              </>
            )}

            {payments.redirectSpec &&
              payments.redirectSpec.type === 'url' &&
              payments.status !== 'deposited' &&
              payments.status !== 'failed' &&
              payments.status !== 'in_process' &&
              payments.status !== 'id_verification' && (
                <>
                  <DefaultButton
                    textId="continueRequest"
                    onClick={() => {
                      const url = payments.redirectSpec.spec;
                      if (url && url.startsWith('/client/pay')) {
                        openRedirectModal(url);
                      } else {
                        window.open(url);
                      }
                    }}
                    filled
                  />
                  {isDeposit && <DefaultButton textId="justBack" type="button" onClick={() => window.history.back()} />}
                </>
              )}
            {/* eslint-enable */}
            {payments &&
              (payments.operation === 'withdraw' ||
                payments.operation === 'wallet_withdraw' ||
                payments.operation === 'waiting_approval') && (
                <>
                  {payments.cancelButton &&
                    !(
                      ['wallet_withdraw', 'withdraw'].includes(payments.operation) &&
                      ['waiting_confirmation', 'processing', 'done', 'payout'].includes(payments.status)
                    ) && <DefaultButton textId="justCancel" onClick={() => cancelWithdrawalClick()} butColor="error" />}
                  {payments.status === 'waiting_verification' && (
                    <>
                      <DefaultButton textId="justVerify" onClick={handleTfaModalToggle} filled />
                      <BasicModal
                        isOpen={tfaModalOpen}
                        modalClassName="TwoStepModal"
                        captionId="authTwoStepAuthentication"
                        onRequestClose={handleTfaModalToggle}>
                        {profile?.tfaSettings?.type === 'email' && (
                          <TwoStepEmailForm
                            isVerify
                            withdrawalId={payments.id}
                            onResend={() => verifyWithdrawalResend.request({ id: payments.id })}
                            onSubmit={verifyWithdrawalRequest}
                            onClose={handleTfaModalToggle}
                          />
                        )}
                        {profile?.tfaSettings?.type === 'totp' && (
                          <TwoStepGoogleForm
                            withdrawalId={payments.id}
                            onSubmit={verifyWithdrawalRequest}
                            onClose={handleTfaModalToggle}
                          />
                        )}
                      </BasicModal>
                    </>
                  )}
                  {payments.status === 'need_finish' && (
                    <DefaultButton textId="justFinish" onClick={() => finishPayment()} filled />
                  )}
                </>
              )}
          </div>

          {/* eslint-disable */}
          {payments.paymentProofEnabled && (
            <PaymentProofForm paymentId={payments.id} isDeposit={isDeposit} />
          )}
          {/* eslint-enable */}

          <UserAmountEditHistory accountId={match.params.id} />
        </div>
        <BasicModal
          captionId="justConfirmationMessageSent"
          isOpen={messageModalOpen}
          onRequestClose={() => setMessageModalOpen(false)}
        />
        <BasicModal
          captionId="justFinishWithdrawal"
          isOpen={infoModalOpen}
          onRequestClose={() => setInfoModalOpen(false)}>
          <SecondPaymentsForm
            paymentForm={paymentFormVals}
            onResetForm={() => setInfoModalOpen(false)}
            closeModal={closeModal}
            isModal
            addons={addons}
            history={window.history}
            isNullForm={!hasAdditionalForm}
            justFinish
          />
        </BasicModal>

        <BasicModal captionId="Pay page" isOpen={redirectModalOpen} onRequestClose={closeRedirectModal}>
          <div style={{ width: '800px', height: '900px', overflow: 'auto' }}>
            <LeanPay
              customerId={queryString.parse(redirectUrl.split('?')[1] || '')?.customer_id}
              depositId={payments?.id || match.params.id}
              appToken={queryString.parse(redirectUrl.split('?')[1] || '')?.app_token}
              sdkToken={queryString.parse(redirectUrl.split('?')[1] || '')?.sdk_token}
              isSandbox={window.location.hostname !== 'my.dolmarkets.com'}
            />
          </div>
        </BasicModal>

        <style jsx global>
          {staticStyles}
        </style>
        <style jsx global>
          {dynamicStyles}
        </style>
      </PageLayout>
    )
  );
};

PaymentsConfirmationPanel.propTypes = {
  payments: PropTypes.shape({
    id: PropTypes.number.isRequired,
    operation: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    accountLogin: PropTypes.string.isRequired,
    amount: MoneyCell.propTypes.value,
    paymentMethodTitle: PropTypes.string.isRequired,
    paymentSystemIconUrl: PropTypes.string.isRequired,
    paymentSystemInstruction: PropTypes.string.isRequired,
    instruction: PropTypes.shape({
      text: PropTypes.string.isRequired,
    }),
    invoiceEnabled: PropTypes.bool.isRequired,
    paymentProofEnabled: PropTypes.bool.isRequired,
    redirectSpec: PropTypes.object,
    paymentMethodRequest: PropTypes.shape({
      id: PropTypes.number,
      invoiceEnabled: PropTypes.bool,
      paymentMethod: PropTypes.number,
      request: PropTypes.object,
      saveRequisites: PropTypes.bool,
      requisitesForm: PropTypes.shape({
        properties: PropTypes.object,
      }),
    }),
  }),
  isConfirmationMessageResent: PropTypes.bool,
  loadPayments: PropTypes.func.isRequired,
  getPaymentsInvoice: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  cancelWithdrawal: PropTypes.func.isRequired,
  showModal: PropTypes.func,
  redirect: PropTypes.bool.isRequired,
  addons: PropTypes.array.isRequired,
  theme: PropTypes.object,
  profile: PropTypes.object.isRequired,
};

PaymentsConfirmationPanel.defaultProps = {
  isConfirmationMessageResent: false,
  showModal: () => {},
  payments: {},
  theme: {},
};

const ThemedPaymentsConfirmationPanel = withTheme()(ModalHOC(PaymentsConfirmationPanel));

export default ThemedPaymentsConfirmationPanel;
export { PaymentsConfirmationPanel };
